/**
 * Auto-tracking React components
 */

import React, { useEffect, useRef } from 'react';
import { useAnalyzify } from './hooks';
import { extractNumericId } from '../adapters/ids';
import { variantIdFromUrl } from '../core/page-key';
import type { StorefrontProduct, StorefrontCart, StorefrontCollection, StorefrontOrder } from '../types/storefront';

/**
 * ProductTracker - Automatically tracks view_item when mounted
 *
 * Pass the selected variant as `variantId`. A different product is a new
 * view_item; another variant of the same product is a variant change
 * (variant_changed — plus view_item when the store's
 * variant_changed_with_view_item setting is on), as on a Liquid theme.
 */
export interface ProductTrackerProps {
  product: StorefrontProduct;
  variantId?: string;
  children?: React.ReactNode;
}

export function ProductTracker({
  product,
  variantId,
  children,
}: ProductTrackerProps): React.JSX.Element | null {
  const { track, isReady, setPageContext } = useAnalyzify();
  // Last tracked product/variant — prevents duplicates in Strict Mode and
  // tells a variant switch apart from a new product view
  const tracked = useRef<{ productId: string; variantKey: string } | null>(null);

  useEffect(() => {
    if (!isReady) return;
    // Variants are compared by numeric ID (the host may pass a GID or a
    // number). No variantId means the variant setPageContext() reports for
    // the landing page: the ?variant= URL parameter, then the first variant —
    // so a variantId that arrives later and names it is not a change.
    const numeric = (id: string | undefined) => (id ? String(extractNumericId(id) || id) : '');
    const variantKey =
      numeric(variantId) ||
      numeric(variantIdFromUrl()) ||
      numeric(product.variants?.edges?.[0]?.node?.id) ||
      'default';
    const last = tracked.current;
    if (last && last.productId === product.id && last.variantKey === variantKey) return;

    tracked.current = { productId: product.id, variantKey };

    if (last && last.productId === product.id) {
      // Same product, another variant selected
      track.variantChange(product, variantKey);
      return;
    }

    // Set page context (the selected variant is what the landing page's
    // automatic view_item reports)
    setPageContext({
      type: 'product',
      product,
      variantId,
    });

    // Track view_item
    track.viewItem(product, variantId);

    // NOTE: Do NOT reset tracked in cleanup - this prevents double-tracking in React Strict Mode
  }, [isReady, product.id, variantId]);

  return <>{children}</>;
}

/**
 * CollectionTracker - Automatically tracks view_item_list when mounted
 */
export interface CollectionTrackerProps {
  collection: StorefrontCollection;
  children?: React.ReactNode;
}

export function CollectionTracker({
  collection,
  children,
}: CollectionTrackerProps): React.JSX.Element | null {
  const { track, isReady, setPageContext } = useAnalyzify();
  // Track which collection has been tracked to prevent duplicates in Strict Mode
  const trackedCollectionId = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || trackedCollectionId.current === collection.id) return;

    trackedCollectionId.current = collection.id;

    // Set page context with collection data
    // This sets window.analyzify.getCollectionObj which CDN scripts expect
    setPageContext({
      type: 'collection',
      collection,
    });

    // Extract products from collection. Guard against partial API data
    // (products/edges absent) so this tracker component never throws into
    // the host tree — matches the defensive setPageContext/adapter contract.
    const products = collection.products?.edges?.map((edge) => edge.node) ?? [];

    // Track view_item_list
    track.viewItemList(products, collection.title, collection.id);

    // NOTE: Do NOT reset trackedCollectionId in cleanup - this prevents double-tracking in React Strict Mode
  }, [isReady, collection.id]);

  return <>{children}</>;
}

/**
 * CartTracker - Automatically tracks view_cart when mounted
 */
export interface CartTrackerProps {
  cart: StorefrontCart;
  children?: React.ReactNode;
}

export function CartTracker({ cart, children }: CartTrackerProps): React.JSX.Element | null {
  const { track, isReady, setPageContext } = useAnalyzify();
  // Track which cart has been tracked to prevent duplicates in Strict Mode
  const trackedCartId = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || trackedCartId.current === cart.id) return;

    trackedCartId.current = cart.id;

    // Set page context
    setPageContext({
      type: 'cart',
      cart,
    });

    // Track view_cart
    track.viewCart(cart);

    // NOTE: Do NOT reset trackedCartId in cleanup - this prevents double-tracking in React Strict Mode
  }, [isReady, cart.id]);

  return <>{children}</>;
}

/**
 * SearchTracker - Automatically tracks search when mounted
 */
export interface SearchTrackerProps {
  term: string;
  results: StorefrontProduct[];
  resultsCount: number;
  children?: React.ReactNode;
}

export function SearchTracker({
  term,
  results,
  resultsCount,
  children,
}: SearchTrackerProps): React.JSX.Element | null {
  const { track, isReady, setPageContext } = useAnalyzify();
  const trackedTerm = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || trackedTerm.current === term) return;

    trackedTerm.current = term;

    // Set page context — with the results, so a search landing page can
    // initialize the platforms
    setPageContext({
      type: 'search',
      search: { term, results, resultsCount },
    });

    // Track search
    track.search(term, results, resultsCount);
  }, [isReady, term]);

  return <>{children}</>;
}

/**
 * AddToCartButton - Button that tracks add_to_cart on click
 */
export interface AddToCartButtonProps {
  product: StorefrontProduct;
  quantity?: number;
  cart: StorefrontCart;
  /** Variant being added (defaults to the product's first variant) */
  variantId?: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function AddToCartButton({
  product,
  quantity = 1,
  cart,
  variantId,
  onClick,
  children,
  className,
  disabled,
}: AddToCartButtonProps): React.JSX.Element {
  const { track, isReady } = useAnalyzify();

  const handleClick = () => {
    if (isReady) {
      track.addToCart(product, quantity, cart, variantId);
    }
    onClick?.();
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// NEW COMPONENTS FOR NEXT.JS APP ROUTER
// ═══════════════════════════════════════════════════════════════

/**
 * HomepageTracker - Automatically tracks homepage event when mounted
 *
 * @example
 * ```tsx
 * // In your homepage (page.tsx)
 * export default function HomePage() {
 *   return (
 *     <HomepageTracker>
 *       <Hero />
 *       <FeaturedProducts />
 *     </HomepageTracker>
 *   );
 * }
 * ```
 */
export interface HomepageTrackerProps {
  children?: React.ReactNode;
}

export function HomepageTracker({ children }: HomepageTrackerProps): React.JSX.Element | null {
  const { track, isReady, setPageContext } = useAnalyzify();
  // Use a boolean ref but don't reset it in cleanup to prevent double-tracking in Strict Mode
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!isReady || hasTracked.current) return;

    hasTracked.current = true;

    // Set page context
    setPageContext({
      type: 'index',
    });

    // Track homepage
    track.homepage();

    // NOTE: Do NOT reset hasTracked in cleanup - this prevents double-tracking in React Strict Mode
  }, [isReady]);

  return <>{children}</>;
}

/**
 * PurchaseTracker - Automatically tracks purchase event when mounted
 *
 * Use on order confirmation / thank you pages
 *
 * @example
 * ```tsx
 * // In your checkout success page
 * export default function CheckoutSuccess({ order }) {
 *   return (
 *     <PurchaseTracker order={order}>
 *       <h1>Thank you for your order!</h1>
 *       <OrderDetails order={order} />
 *     </PurchaseTracker>
 *   );
 * }
 * ```
 */
export interface PurchaseTrackerProps {
  order: StorefrontOrder;
  children?: React.ReactNode;
}

export function PurchaseTracker({
  order,
  children,
}: PurchaseTrackerProps): React.JSX.Element | null {
  const { track, isReady, setPageContext } = useAnalyzify();
  const trackedOrderId = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || trackedOrderId.current === order.id) return;

    trackedOrderId.current = order.id;

    // Set page context
    setPageContext({
      type: 'other', // Thank you page is 'other' type
    });

    // Track purchase
    track.purchase(order);
  }, [isReady, order.id]);

  return <>{children}</>;
}

/**
 * SelectItemTracker - Wrapper that tracks select_item on click
 *
 * Use in collection/search pages when user clicks on a product.
 * Supports keyboard navigation for accessibility.
 *
 * @example
 * ```tsx
 * {products.map((product, index) => (
 *   <SelectItemTracker
 *     key={product.id}
 *     product={product}
 *     listName="Collection: Summer Sale"
 *     index={index}
 *   >
 *     <ProductCard product={product} />
 *   </SelectItemTracker>
 * ))}
 * ```
 *
 * @example Using as a different element
 * ```tsx
 * <SelectItemTracker
 *   as="article"
 *   product={product}
 *   listName="Search Results"
 * >
 *   <ProductCard product={product} />
 * </SelectItemTracker>
 * ```
 */
export interface SelectItemTrackerProps {
  product: StorefrontProduct;
  listName: string;
  index?: number;
  /** List identifier, e.g. the collection ID (defaults to the current collection) */
  listId?: string | number;
  children: React.ReactNode;
  className?: string;
  /** The element type to render. Defaults to 'div'. */
  as?: keyof React.JSX.IntrinsicElements;
}

export function SelectItemTracker({
  product,
  listName,
  index,
  listId,
  children,
  className,
  as: Component = 'div',
}: SelectItemTrackerProps): React.JSX.Element {
  const { track, isReady } = useAnalyzify();

  const handleClick = () => {
    if (isReady) {
      try {
        track.selectItem(product, listName, index, listId);
      } catch (error) {
        console.error('[Analyzify] Failed to track select_item:', error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const Wrapper = Component as React.ElementType;
  return (
    <Wrapper
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      role="button"
      tabIndex={0}
    >
      {children}
    </Wrapper>
  );
}

/**
 * BeginCheckoutButton - Button that tracks begin_checkout on click
 *
 * @example
 * ```tsx
 * <BeginCheckoutButton
 *   cart={cart}
 *   onClick={() => router.push(cart.checkoutUrl)}
 * >
 *   Proceed to Checkout
 * </BeginCheckoutButton>
 * ```
 */
export interface BeginCheckoutButtonProps {
  cart: StorefrontCart;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function BeginCheckoutButton({
  cart,
  onClick,
  children,
  className,
  disabled,
}: BeginCheckoutButtonProps): React.JSX.Element {
  const { track, isReady } = useAnalyzify();

  const handleClick = () => {
    if (isReady) {
      track.beginCheckout(cart);
    }
    onClick?.();
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
